/**
 * Wave eeeee · Global-search client helpers.
 *
 * Responsibilities:
 *   1. Define the canonical result types for the command bar.
 *   2. Map each result kind to an in-app href.
 *   3. Manage a recently-visited list in localStorage so the
 *      command bar has something to show before the user types.
 */

// ─── result types ─────────────────────────────────────────────────────────────

export type GlobalResultKind =
  | 'project'
  | 'estimate'
  | 'budgetItem'
  | 'po'
  | 'supplier'
  | 'rdo'
  | 'fvs'
  | 'drawing'
  | 'rfi';

export interface GlobalHit {
  kind: GlobalResultKind;
  id: string;
  projectId?: string;
  /** Primary display label. */
  title: string;
  /** Secondary display label (project name, status, etc.). */
  subtitle?: string;
}

// ─── response shape from GET /v1/search ───────────────────────────────────────

export interface GlobalSearchResponse {
  projects: Array<{ id: string; name: string; address?: string }>;
  estimates: Array<{ id: string; projectId: string; projectName: string; name: string; status: string }>;
  budgetItems: Array<{ id: string; projectId: string; description: string; totalCents: number }>;
  pos: Array<{ id: string; projectId: string; code: string; supplierName: string }>;
  suppliers: Array<{ id: string; name: string; category?: string }>;
  rdos: Array<{ id: string; projectId: string; logDate: string; workPerformed: string }>;
  fvs: Array<{ id: string; projectId: string; code?: string; name: string; status: string }>;
  drawings: Array<{ id: string; projectId: string; code: string; name: string }>;
  rfis: Array<{ id: string; projectId: string; code: string; title: string; status: string }>;
}

/**
 * Flatten the grouped API response into a single list of GlobalHit objects.
 * Order mirrors KIND_ORDER in CommandBar so groups appear consistently.
 */
export function flattenResults(data: GlobalSearchResponse): GlobalHit[] {
  const hits: GlobalHit[] = [];

  for (const p of data.projects) {
    hits.push({ kind: 'project', id: p.id, title: p.name, subtitle: p.address });
  }
  for (const e of data.estimates) {
    hits.push({ kind: 'estimate', id: e.id, projectId: e.projectId, title: e.name, subtitle: `${e.projectName} · ${e.status}` });
  }
  for (const b of data.budgetItems) {
    hits.push({ kind: 'budgetItem', id: b.id, projectId: b.projectId, title: b.description });
  }
  for (const po of data.pos) {
    hits.push({ kind: 'po', id: po.id, projectId: po.projectId, title: po.code, subtitle: po.supplierName });
  }
  for (const s of data.suppliers) {
    hits.push({ kind: 'supplier', id: s.id, title: s.name, subtitle: s.category });
  }
  for (const r of data.rdos) {
    hits.push({ kind: 'rdo', id: r.id, projectId: r.projectId, title: r.logDate, subtitle: r.workPerformed.slice(0, 80) });
  }
  for (const f of data.fvs) {
    hits.push({ kind: 'fvs', id: f.id, projectId: f.projectId, title: f.name, subtitle: f.status });
  }
  for (const d of data.drawings) {
    hits.push({ kind: 'drawing', id: d.id, projectId: d.projectId, title: `${d.code} — ${d.name}` });
  }
  for (const r of data.rfis) {
    hits.push({ kind: 'rfi', id: r.id, projectId: r.projectId, title: `${r.code} — ${r.title}`, subtitle: r.status });
  }

  return hits;
}

/** Map a GlobalHit to its canonical in-app destination URL. */
export function hitHref(hit: GlobalHit): string {
  const pid = hit.projectId ?? '';
  switch (hit.kind) {
    case 'project':
      return `/projects/${hit.id}`;
    case 'estimate':
      return `/projects/${pid}/trena/${hit.id}`;
    case 'budgetItem':
      return `/projects/${pid}/orcamento`;
    case 'po':
      return `/projects/${pid}/compras/${hit.id}`;
    case 'supplier':
      return `/fornecedores?focus=${encodeURIComponent(hit.id)}`;
    case 'rdo':
      return `/projects/${pid}/rdo`;
    case 'fvs':
      return `/projects/${pid}/qualidade/fvs/${hit.id}`;
    case 'drawing':
      return `/projects/${pid}/pranchas/${hit.id}`;
    case 'rfi':
      return `/projects/${pid}/pranchas?rfi=${hit.id}`;
    default:
      return '/';
  }
}

// ─── recently-visited surface ─────────────────────────────────────────────────

const RECENT_KEY = 'builder.cmd.recent';
const RECENT_MAX = 8;

export interface RecentEntry {
  kind: GlobalResultKind;
  id: string;
  projectId?: string;
  title: string;
  subtitle?: string;
  href: string;
  visitedAt: string; // ISO
}

export function getRecentEntries(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as RecentEntry[];
  } catch {
    return [];
  }
}

export function pushRecentEntry(hit: GlobalHit): void {
  try {
    const entries = getRecentEntries().filter((e) => !(e.kind === hit.kind && e.id === hit.id));
    const entry: RecentEntry = {
      kind: hit.kind,
      id: hit.id,
      projectId: hit.projectId,
      title: hit.title,
      subtitle: hit.subtitle,
      href: hitHref(hit),
      visitedAt: new Date().toISOString(),
    };
    const updated = [entry, ...entries].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
  } catch {
    // localStorage unavailable — no-op
  }
}
